import { environmentHttpClient } from "client/Api/httpClient";
import {
	EmailImportance,
	ExchangeEmailId,
	ExchangeFolderId,
	ExchangeMessageBodyKind,
	MailMessageLocationModel,
	MailMessageLocationQueryModel,
	SignatureKind
} from "./common";
import {
	DraftEmailAttachment,
	DraftEmailModel,
	SendOrSaveEmailDraftModel
} from "./respondToEmail";
import { FontStyleModel } from "../common";

export enum EmailFilterSelector {
	Unread = 1,
	Flagged = 2,
	HasAttachment = 3
}

export enum EmailSortOrder {
	None = 1,
	Date = 2,
	From = 3,
	Subject = 4
}

export enum EmailSortDirection {
	Default = 0,
	Ascending = 1,
	Descending = 2
}

export enum MessageReadState {
	Unread = 0,
	Read = 1
}

export enum MessageFlagState {
	NotFlagged = 0,
	Flagged = 1
}

export enum EmailDateFilterKind {
	Any = 0,
	Past24Hours = 1,
	PastWeek = 2,
	PastMonth = 3,
	Past3Months = 4,
	PastYear = 5
}

export function setExchangeReadStatus(
	selection: MessageSelectionModel,
	state: MessageReadState
): Promise<void> {
	return environmentHttpClient.put<void>(
		"Email/SetExchangeReadStatus",
		{ selection, state }
	);
}

export function setExchangeFlagStatus(
	selection: MessageSelectionModel,
	state: MessageFlagState
): Promise<void> {
	return environmentHttpClient.put<void>(
		"Email/SetExchangeFlagStatus",
		{ selection, state }
	);
}

export type UploadingFinishedAttachmentModel = {
	uid: string;
	attachment: DraftEmailAttachment;
};

export type UploadAttachmentErrorModel = {
	errorReason: string;
	fileUid: string;
};

export type UploadAttachmentModel = {
	draft: MailMessageLocationQueryModel;
	fileUid: string;
};

export async function uploadAttachment(
	model: UploadAttachmentModel,
	file: File,
	signal?: AbortSignal
): Promise<UploadingFinishedAttachmentModel> {
	const formData = new FormData();
	formData.append("draft.smtpAddress", model.draft.smtpAddress ?? "");
	formData.append("draft.messageId", model.draft.messageId);
	formData.append("fileUid", model.fileUid);
	formData.append("file", file);

	return await environmentHttpClient.postFormData<UploadingFinishedAttachmentModel>(
		"Email/UploadAttachment",
		formData,
		{ signal }
	);
}

export type DeleteAttachmentRequestModel = {
	message: MailMessageLocationModel;
	exchangeAttachmentId: string;
};

export async function deleteAttachment(
	model: DeleteAttachmentRequestModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		"Email/DeleteAttachment",
		model,
		{ signal }
	);
}

export type InsertEmbeddedImageResponseModel = {
	attachment: DraftEmailAttachment;
};

export interface ExchangeEmailScreenSelectionModel {
	folder: MailFolderLocationModel;
	sortOrder: EmailSortOrder;
	sortDirection: EmailSortDirection;
	searchQuery: string;
	filterSelector: EmailFilterSelector | null;
}

export interface ListFromExchangePossibleWithFolderCountersResponse {
	hasMore: boolean;
	data: readonly EmailListItemModel[];
	watermark: string | null;
	folderTotalCount: number | null;
	folderUnreadCount: number | null;
}

export function listFromExchange(
	pageSize: number,
	skip: number,
	criteria: ExchangeEmailScreenSelectionModel,
	signal?: AbortSignal
): Promise<ListFromExchangePossibleWithFolderCountersResponse> {
	return environmentHttpClient.post<ListFromExchangePossibleWithFolderCountersResponse>(
		"Email/ListFromExchange",
		{ pageSize, skip, criteria },
		{ signal }
	);
}

export function listFromExchangeWithFolderCounters(
	pageSize: number,
	skip: number,
	criteria: ExchangeEmailScreenSelectionModel,
	signal?: AbortSignal
): Promise<ListFromExchangePossibleWithFolderCountersResponse> {
	return environmentHttpClient.post<ListFromExchangePossibleWithFolderCountersResponse>(
		"Email/ListFromExchangeWithFolderCounters",
		{ pageSize, skip, criteria },
		{ signal }
	);
}

export interface ExchangeFolderWatermarkModel {
	folder: MailFolderLocationModel;
	watermark: string | null;
}

export interface GetEmailChangesRequest {
	countersRequests: MailFolderLocationModel[];
	newMessageRequest: ExchangeFolderWatermarkModel;
}

export interface EmailChanges {
	folderId: ExchangeFolderId;
	watermark: string | null;
	changes: EmailListItemModel[];
}

export interface FolderCounters {
	folderId: ExchangeFolderId;
	unread: number;
	total: number;
}


export interface GetEmailChangesResponse {
	emailChanges: EmailChanges | null;
	counters: FolderCounters[];
}

export type NormalAttachmentModel = {
	exchangeAttachmentId: string;
	name: string;
};

export type AttachmentModel = NormalAttachmentModel;

export interface EmailModel {
	exchangeEmailId: string;
	ownerName: string;
	from: string;
	subject: string;
	sentDateTime: Date | string | null;
	receivedDateTime: Date | string | null;
	isRead: boolean;
	isFlagged: boolean;
	body: string;
	bodyType: ExchangeMessageBodyKind;
	to: string;
	cc: string;
	attachments: AttachmentModel[];
	messageType: MessageType;
	lastAction: EmailActions;
	lastActionDateTime: Date | string | null;
	importance: EmailImportance;
	parentFolderId: ExchangeFolderId;

	isMeetingMessage: boolean;
	start: string;
	end: string;
	appointmentId: string;
	location: string;
	isResponseRequested: boolean;
}

export function getNewEmailsForFolderFromExchange(
	request: GetEmailChangesRequest,
	signal?: AbortSignal
): Promise<GetEmailChangesResponse> {
	return environmentHttpClient.post<GetEmailChangesResponse>(
		"Email/GetNewEmailsForFolderFromExchange",
		request,
		{ signal }
	);
}

export function getEmailScreenModel(signal?: AbortSignal): Promise<EmailScreenModel> {
	return environmentHttpClient.get<EmailScreenModel>(
		"Email/GetEmailScreenModel",
		undefined,
		{ signal }
	);
}

export function sendEmail(
	model: SendOrSaveEmailDraftModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		"Email/SendExchangeEmailAndRemoveDraft",
		model,
		{ signal }
	);
}


export type SaveDraftResponseModel = {
	exchangeId: ExchangeEmailId;
};

export function saveOrUpdateExchangeDraft(
	model: SendOrSaveEmailDraftModel,
	signal?: AbortSignal
): Promise<SaveDraftResponseModel> {
	return environmentHttpClient.post<SaveDraftResponseModel>(
		"Email/SaveOrUpdateExchangeDraft",
		model,
		{ signal }
	);
}

export function getDraftById(location: MailMessageLocationModel, signal?: AbortSignal): Promise<DraftEmailModel> {
	return environmentHttpClient.post<DraftEmailModel>(
		"Email/GetExchangeDraftEmail",
		location,
		{ signal }
	);
}

export type MessageSelectionModel = {
	smtpAddress: OptionalModel<string>;
	emailIds: readonly ExchangeEmailId[];
};

export function deleteExchangeEmailByIds(
	selection: MessageSelectionModel,
	softDelete: boolean
): Promise<void> {
	return environmentHttpClient.post<void>(
		"Email/DeleteExchangeEmailByIds",
		{
			selection,
			softDelete
		}
	);
}

export function openExchangeEmailByExchangeId(
	location: MailMessageLocationModel,
	signal?: AbortSignal
): Promise<EmailModel> {
	return environmentHttpClient.post<EmailModel>(
		"Email/OpenExchangeEmailByExchangeId",
		location,
		{ signal }
	);
}

export type MailFolderLocationModel = {
	smtpAddress: OptionalModel<string>;
	folderId: string;
};

export function markAllAsRead(location: MailFolderLocationModel, signal?: AbortSignal): Promise<void> {
	return environmentHttpClient.post<void>(
		"/Email/MarkAllExchangeItemsAsRead",
		location,
		{ signal }
	);
}

export function markAllAsUnread(location: MailFolderLocationModel, signal?: AbortSignal): Promise<void> {
	return environmentHttpClient.post<void>(
		"/Email/MarkAllExchangeItemsAsUnread",
		location,
		{ signal }
	);
}

export async function insertEmbedImageToExchange(
	draft: MailMessageLocationQueryModel,
	file: File,
	signal?: AbortSignal
): Promise<InsertEmbeddedImageResponseModel> {
	const formData = new FormData();
	formData.append("smtpAddress", draft.smtpAddress ?? "");
	formData.append("messageId", draft.messageId);
	formData.append("file", file);

	return await environmentHttpClient.postFormData<UploadingFinishedAttachmentModel>(
		"Email/InsertEmbeddedImageToExchange",
		formData,
		{ signal }
	);
}

export type CreateFolderModel = {
	folderName: string;
	location: MailFolderOrRootLocationModel;
};

export function createFolder(
	model: CreateFolderModel,
	signal?: AbortSignal
): Promise<IFolderViewModel<EagerLoadedChildren>> {
	return environmentHttpClient.post<IFolderViewModel<EagerLoadedChildren>>(
		"Email/CreateFolder",
		model,
		{ signal }
	);
}

export interface RenameFolderModel {
	location: MailFolderLocationModel;
	newFolderName: string;
}

export function renameFolder(
	model: RenameFolderModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		"Email/RenameFolder",
		model,
		{ signal }
	);
}

export interface MoveEmailsToFolderModel {
	destinationFolderId: ExchangeFolderId;
	selection: MessageSelectionModel;
}

export function moveEmailToFolder(model: MoveEmailsToFolderModel): Promise<void> {
	return environmentHttpClient.post<void>("Email/MoveEmailsToFolder", model);
}

export interface DeleteFolderModel {
	location: MailFolderLocationModel;
	moveToDeletedItems: boolean;
}

export function deleteEmailFolder(
	model: DeleteFolderModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.delete<void>(
		"Email/DeleteFolder",
		model,
		{ signal }
	);
}

export type MailFolderLocationQueryModel = {
	smtpAddress: string | null;
	folderId: string;
};

export type MailFolderBreadcrumbItemModel = {
	folderId: ExchangeFolderId;
	name: string;
};

export function getFolderBreadcrumbs(
	location: MailFolderLocationQueryModel,
	signal?: AbortSignal
): Promise<readonly MailFolderBreadcrumbItemModel[]> {
	return environmentHttpClient.get<readonly MailFolderBreadcrumbItemModel[]>(
		"Email/GetFolderBreadcrumbs",
		location,
		{ signal }
	);
}

export type MailFolderOrRootLocationModel = {
	smtpAddress: OptionalModel<string>;
	folderId: OptionalModel<string>;
};

export function listFolders(
	mailbox: MailFolderOrRootLocationModel,
	signal?: AbortSignal
): Promise<readonly IFolderViewModel<LazyLoadedChildren>[]> {
	return environmentHttpClient.post<readonly IFolderViewModel<LazyLoadedChildren>[]>(
		"Email/ListFolders",
		mailbox,
		{ signal }
	);
}

export interface ExchangeMailboxModel {
	email: string;
}

export function getExchangeMailboxes(
	signal?: AbortSignal
): Promise<readonly ExchangeMailboxModel[]> {
	return environmentHttpClient.post<readonly ExchangeMailboxModel[]>(
		"Email/GetExchangeMailboxes",
		undefined,
		{ signal }
	);
}

export enum EmailActions {
	No = 0,
	Replied = 1,
	Forwarded = 2
}

export enum MessageType {
	Email = 0,
	MeetingRequest = 1,
	MeetingResponse = 2,
	MeetingCancellation = 3
}

export interface EmailListItemModel {
	emailId: ExchangeEmailId;
	from: string;
	to: string;
	subject: string;
	sentDateTime: string | null;
	receivedDateTime: string | null;
	isRead: boolean;
	isFlagged: boolean;
	hasAttachments: boolean;
	ownerName: string | null;
	isDraft: boolean;
	messageType: MessageType;
	lastAction: EmailActions;
	importance: EmailImportance;
	isMeetingMessage: boolean;
	preview: string | null;
}

export interface FolderActionOptions {
	canCreate: boolean;
	canDelete: boolean;
	canRenameOrMove: boolean;
}

export const enum FolderChildrenKind {
	LazyLoaded = 0,
	EagerLoaded = 1
}

export type LazyLoadedChildren = {
	kind: FolderChildrenKind.LazyLoaded;
	hasChildren: boolean;
};

export type EagerLoadedChildren = {
	kind: FolderChildrenKind.EagerLoaded;
	children: readonly IFolderViewModel<EagerLoadedChildren>[];
};

export type FolderChildren = LazyLoadedChildren | EagerLoadedChildren;

export interface IFolderViewModel<TChildren extends FolderChildren> {
	folderId: ExchangeFolderId;
	name: string;
	parentFolderId: ExchangeFolderId | null;
	unreadCount: number;
	totalCount: number;
	isDrafts: boolean;
	isSent: boolean;
	isDeleted: boolean;
	isInbox: boolean;
	isJunkMail: boolean;
	exchangeFolderId: string;
	folderActionOptions: FolderActionOptions;
	children: TChildren;
}

export interface ExchangeAccountModel {
	folders: readonly IFolderViewModel<LazyLoadedChildren>[];
	rootFolderId: ExchangeFolderId | null;
	rootFolderActionOptions: FolderActionOptions;
	draftsFolderId: ExchangeFolderId | null;
	sentFolderId: ExchangeFolderId | null;
	junkEmailFolderId: ExchangeFolderId | null;
	emailAddress: string;
	isFaulted: boolean;
	isAutoMarkAsReadEnabled: boolean;
}

export interface EmailAddressModel {
	displayName: string;
	emailAddress: string;
	isDefault: boolean;
}

export interface SignatureModel {
	kind: SignatureKind;
	name: string;
	content: string;
	isDefault: boolean;
}

export interface AvailableSignaturesModel {
	personal: SignatureModel | null;
	managed: SignatureModel | null;
}

export interface EmailScreenModel {
	exchangeAccounts: readonly ExchangeAccountModel[];
	availableFroms: EmailAddressModel[];
	signatures: AvailableSignaturesModel;
	showGoToOutlookAction: boolean;
	millisecondsBetweenRetrievingEmailChanges: number;
	saveDraftsInterval: number;
	exchangeAttachmentSizeLimit: number;
	pageSize: number;
	useIntegrationCredentials: boolean;
	automaticallyShowSenderImages: boolean;
	defaultComposerFontStyle: FontStyleModel;
}

export enum ExchangeConfigurationErrorKind {
	ExchangeIsNotConfigured = 1,
	AuthenticationError = 2
}

export type ExchangeConfigurationErrorResponseModel = {
	errorKind: ExchangeConfigurationErrorKind;
};